import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { ToastActionButton } from '@heidi/design-system/toast';
import { useOpenEvidenceUpgradeModal } from '@/features/growth/deprecate-evidence-plus/useOpenEvidenceUpgradeModal';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import useReverseTrial from '@/hooks/useReverseTrial';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

export interface EvidenceQuotaConsumedToastUpgradeButtonProps {
  /**
   * Whether the user's upgrade path is Practice rather than Clinician. Passed
   * from the caller so the tier-coloured button recipe and tier icon stay in
   * sync with the toast container tone decided by
   * `fireEvidenceQuotaConsumedToast`.
   */
  isPracticeUpgradeTarget: boolean;
}

/**
 * Compact "Upgrade" pill rendered in the System Toast `action` slot
 * (Figma nodes `10492:57298` Clinician / `10742:53227` Practice). Routes the
 * click to the tier-correct upgrade modal; the toast then auto-dismisses on
 * its own timer or is covered by the modal overlay.
 *
 * Uses `ToastActionButton` so the per-tone Button recipe (`variant="pro"` for
 * Clinician / `variant="pro_plus"` for Practice, inside an outline toast) is
 * picked by the design system rather than hand-rolled here. The tier glyph
 * inside the button is the DS `TierProFilled` / `TierPracticeFilled` — the
 * same icon the toast's leading slot uses via the `pro` / `practice` tone —
 * so the two marks match. Sizing and colour come from the Button recipe, so
 * no per-icon overrides are needed: the icon follows the Button label, white
 * by default and black under the sky-blue tier override in dark mode (behind
 * the `release-heidi-2-skies-tier-color` flag).
 *
 * Label is intentionally just "Upgrade" — Figma keeps it terse for the
 * compact toast footprint; the popover surface uses longer copy
 * ("Upgrade to Clinician" / "Upgrade to Practice") where space allows.
 *
 * Tier-routing logic is inlined here (and mirrored in `useChatStream` and
 * `CreditsRemainingPopover`) to keep this PR purely additive — extract into
 * a shared `useEvidenceQuotaUpgradeModal` hook when a 4th consumer appears.
 */
export function EvidenceQuotaConsumedToastUpgradeButton({
  isPracticeUpgradeTarget,
}: Readonly<EvidenceQuotaConsumedToastUpgradeButtonProps>) {
  const { data: newUserTier } = useQueryUserBillingInfo((user) => user?.new_product_tier);
  const { isClinicianTrialUser } = useReverseTrial();
  const { openEvidenceUpgradeModal } = useOpenEvidenceUpgradeModal();

  const isScribePlusUser = newUserTier === SubscriptionTier.PRO;

  const openUpgradeModal = useCallback(() => {
    openEvidenceUpgradeModal({
      isScribePlusUser,
      isClinicianTrialUser,
      isPracticeUpgradeTarget,
    });
  }, [
    isClinicianTrialUser,
    isPracticeUpgradeTarget,
    isScribePlusUser,
    openEvidenceUpgradeModal,
  ]);

  return (
    <ToastActionButton
      toastVariant="outline"
      tone={isPracticeUpgradeTarget ? 'practice' : 'pro'}
      onClick={openUpgradeModal}
      className="shrink-0"
    >
      {isPracticeUpgradeTarget ? (
        <TierPracticeFilled data-testid="quota-upgrade-tier-icon-practice" />
      ) : (
        <TierProFilled data-testid="quota-upgrade-tier-icon-pro" />
      )}
      <FormattedMessage
        id="evQtTstUpg"
        defaultMessage="Upgrade"
        description="CTA label on the post-consume evidence quota toast"
      />
    </ToastActionButton>
  );
}
